import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import CrmFreshnessBanner from "../components/CrmFreshnessBanner";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import ElasticInactive from "../components/ElasticInactive";
import CrmAiAsk from "../components/CrmAiAsk";
import { StoreFilter } from "../components/StoreFilter";
import StoreCompareChart from "../components/StoreCompareChart";
import { WeekdayFilter, weekdaysParam } from "../components/WeekdayFilter";
import { HoursFilter } from "../components/HoursFilter";
import { SavedFilters, normalizeFilter, periodDaysOf, rangeFromDays } from "../components/SavedFilters";
import { BarChart3, RefreshCw, Download, Settings2 } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area, Legend, PieChart, Pie, Cell,
} from "recharts";

const csvDownload = (filename, header, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [header, ...rows].map((r) => r.map(esc).join(";")).join("\n");
  const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
};

const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => Number(v || 0).toLocaleString("it-IT");
const inputCls = "bg-zinc-950 border-zinc-800 font-mono";
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);
const COLORS = ["#f59e0b", "#3b82f6", "#10b981", "#ef4444", "#a855f7", "#14b8a6", "#f97316"];
const TT = { contentStyle: { background: "#09090b", border: "1px solid #27272a", fontSize: 12, borderRadius: 8 } };
const AX = { tick: { fontSize: 10, fill: "#71717a" }, tickLine: false, axisLine: false };

function Kpi({ label, value, sub, testid, delta }) {
  const d = delta == null || !isFinite(delta) ? null : delta;
  return (
    <div className="card-elev px-4 py-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-xl font-bold font-mono mt-0.5">{value ?? "—"}</div>
      {d !== null && (
        <div data-testid={`${testid}-delta`} className={`text-[11px] font-semibold ${d > 0 ? "text-emerald-400" : d < 0 ? "text-red-400" : "text-zinc-500"}`}>
          {d > 0 ? "▲" : d < 0 ? "▼" : "="} {Math.abs(d).toFixed(1)}% vs periodo prec.
        </div>
      )}
      {sub && <div className="text-[11px] text-zinc-500">{sub}</div>}
    </div>
  );
}
const pct = (cur, prev) => (prev ? ((Number(cur || 0) - Number(prev)) / Number(prev)) * 100 : null);

function Panel({ title, children, testid, action }) {
  return (
    <div className="card-elev p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Table({ cols, rows, testid, rowKey }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-zinc-800">
      <table className="w-full text-xs" data-testid={testid}>
        <thead className="bg-zinc-950 text-zinc-500 uppercase tracking-wider text-[10px]">
          <tr>{cols.map((c) => <th key={c.k} className={`p-2 ${c.r ? "text-right" : "text-left"}`}>{c.l}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={rowKey ? r[rowKey] : i} className="border-t border-zinc-800/60 hover:bg-zinc-900/40">
              {cols.map((c) => <td key={c.k} className={`p-2 ${c.r ? "text-right font-mono" : ""}`}>{c.f ? c.f(r[c.k], r, i) : r[c.k]}</td>)}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={cols.length} className="p-3 text-center text-zinc-500">Nessun dato nel periodo</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function CrmDashboard() {
  const [range, setRange] = useState({ date_from: daysAgo(30), date_to: today() });
  const [fidOnly, setFidOnly] = useState(true);
  const [stores, setStores] = useState([]);
  const [weekdays, setWeekdays] = useState([]);
  const [hours, setHours] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const reqId = useRef(0);
  const load = async (r = range, f = fidOnly, st = stores, wd = weekdays, hr = hours) => {
    const id = ++reqId.current;
    setBusy(true); setError(null);
    try { const { data } = await api.get("/elastic/analytics", { params: { ...r, fidelity_only: f, stores: st.length ? st.join("|") : undefined, weekdays: weekdaysParam(wd), hours: hr || undefined } }); if (id === reqId.current) setData(data); }
    catch (e) { if (id === reqId.current) { setError(formatApiError(e)); toast.error(formatApiError(e)); } }
    finally { if (id === reqId.current) setBusy(false); }
  };
  const applySaved = (f) => {
    const r = f.period_days ? rangeFromDays(f.period_days) : range;
    setRange(r); setStores(f.stores); setWeekdays(f.weekdays); setHours(f.hours); setFidOnly(f.fidelity_only);
    load(r, f.fidelity_only, f.stores, f.weekdays, f.hours);
  };
  // All'apertura: se esiste un filtro predefinito lo applica, altrimenti carica con i filtri base
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/elastic/saved-filters").then(({ data }) => { const d = (data || []).find((f) => f.is_default); if (d) applySaved(normalizeFilter(d)); else load(); }).catch(() => load()); }, []);

  const preset = (n) => { const r = { date_from: daysAgo(n), date_to: today() }; setRange(r); load(r); };
  const s = data?.summary || {};
  const p = data?.previous;
  const peakHour = data ? [...data.hours].sort((a, b) => b.receipts - a.receipts)[0] : null;
  const peakDay = data ? [...data.weekdays].sort((a, b) => b.receipts - a.receipts)[0] : null;
  const trend = data ? (data.days.length > 62 ? data.months.map((m) => ({ label: m.month, receipts: m.receipts, total_eur: m.total_eur, cards: m.cards })) : data.days.map((d) => ({ label: d.date.slice(5), receipts: d.receipts, total_eur: d.total_eur, cards: d.cards, points: d.points, points_used: d.points_used }))) : [];
  const exportProducts = () => csvDownload("crm_top_prodotti.csv", ["Prodotto", "Incasso", "Quantità", "Righe", "Clienti"], data.top_products.map((p) => [p.name, p.revenue, p.qty, p.lines, p.customers]));
  const exportCustomers = () => csvDownload("crm_top_clienti.csv", ["Carta", "Cliente", "Città", "Scontrini", "Speso", "Punti", "Ultima visita"], data.top_customers.map((c) => [c.card, c.name, c.city, c.receipts, c.total_eur, c.points, c.last_visit]));

  return (
    <div className="space-y-5" data-testid="crm-dashboard">
      <CrmFreshnessBanner />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><BarChart3 className="h-6 w-6 text-[color:var(--paprika)]" /> Dashboard CRM (Elastic)</h1>
          <p className="text-sm text-zinc-500 mt-1">Analisi in tempo reale degli scontrini del CRM del cliente: vendite, abitudini, prodotti e clienti. Sola lettura.</p>
        </div>
        <Link to="/elastic" className="text-xs text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1" data-testid="crm-dash-settings-link"><Settings2 className="h-3.5 w-3.5" /> Connessione e import</Link>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="card-elev p-4 flex flex-wrap items-end gap-3" data-testid="crm-dash-filters">
        <div className="flex gap-1">
          {[["7 gg", 7], ["30 gg", 30], ["90 gg", 90], ["1 anno", 365]].map(([l, n]) => (
            <Button key={n} type="button" size="sm" variant="outline" onClick={() => preset(n)} data-testid={`crm-dash-preset-${n}`} className="border-zinc-800">{l}</Button>
          ))}
        </div>
        <div><Label className="text-zinc-400 text-xs">Dal</Label><Input type="date" data-testid="crm-dash-from" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} className={inputCls} /></div>
        <div><Label className="text-zinc-400 text-xs">Al</Label><Input type="date" data-testid="crm-dash-to" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} className={inputCls} /></div>
        <label className="flex items-center gap-2 text-xs h-10"><input type="checkbox" checked={fidOnly} onChange={(e) => { setFidOnly(e.target.checked); load(range, e.target.checked); }} data-testid="crm-dash-fidelity" className="h-4 w-4" /> Solo clienti fedeltà</label>
        <StoreFilter value={stores} onChange={(v) => { setStores(v); load(range, fidOnly, v); }} />
        <WeekdayFilter value={weekdays} onChange={(v) => { setWeekdays(v); load(range, fidOnly, stores, v); }} testid="crm-dash-weekdays" />
        <HoursFilter value={hours} onChange={(v) => { setHours(v); load(range, fidOnly, stores, weekdays, v); }} testid="crm-dash-hours" />
        <SavedFilters withDefault value={{ stores, weekdays, hours, fidelity_only: fidOnly, period_days: periodDaysOf(range), date_range_custom: true }} onApply={applySaved} testid="crm-dash-saved" />
        <Button type="submit" disabled={busy} data-testid="crm-dash-refresh" className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"><RefreshCw className={`h-4 w-4 mr-1 ${busy ? "animate-spin" : ""}`} /> Aggiorna</Button>
      </form>

      {error && <div className="card-elev p-4 text-sm text-red-400" data-testid="crm-dash-error">{error} — configura la connessione in <Link to="/elastic" className="underline">CRM Elastic</Link>.</div>}
      {!error && data && <CrmAiAsk range={range} fidelityOnly={fidOnly} stores={stores} weekdays={weekdaysParam(weekdays)} hours={hours || null} />}
      {!error && data && data.by_store?.length > 1 && (
        <section className="card-elev p-4 space-y-2" data-testid="crm-dash-stores">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold text-base">Punti vendita {stores.length ? `(${stores.length} selezionati)` : `(${data.by_store.length})`}</h2>
            <Button size="sm" variant="ghost" onClick={() => csvDownload("crm_punti_vendita.csv", ["Punto vendita", "Scontrini", "Incasso", "Scontrino medio", "Carte", "Punti"], data.by_store.map((b) => [b.name, b.receipts, b.total_eur, b.avg_ticket, b.cards, b.points]))} data-testid="crm-dash-stores-csv"><Download className="h-3.5 w-3.5 mr-1" /> CSV</Button>
          </div>
          <Table testid="crm-dash-stores-table" rowKey="name" rows={data.by_store} cols={[
            { k: "name", l: "Punto vendita", f: (v) => <button type="button" className="text-left hover:underline" onClick={() => { const nv = [v]; setStores(nv); load(range, fidOnly, nv); }} title="Filtra su questo punto vendita">{v}</button> },
            { k: "receipts", l: "Scontrini", r: true, f: (v) => v.toLocaleString("it-IT") },
            { k: "total_eur", l: "Incasso", r: true, f: eur },
            { k: "avg_ticket", l: "Scontrino medio", r: true, f: eur },
            { k: "cards", l: "Carte", r: true, f: (v) => v.toLocaleString("it-IT") },
            { k: "points", l: "Punti", r: true, f: (v) => Math.round(v).toLocaleString("it-IT") },
          ]} />
        </section>
      )}
      {!error && data && <StoreCompareChart range={range} fidelityOnly={fidOnly} stores={stores} weekdays={weekdays} hours={hours} />}

      {data && (
        <>
          {p && <div className="text-[11px] text-zinc-500" data-testid="crm-dash-previous-period">Variazioni % rispetto al periodo precedente ({p.from} → {p.to}: {num(p.receipts)} scontrini, {eur(p.total_eur)})</div>}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2" data-testid="crm-dash-kpis">
            <Kpi label="Scontrini" value={num(s.receipts)} delta={pct(s.receipts, p?.receipts)} testid="crm-kpi-receipts" />
            <Kpi label="Incasso" value={eur(s.total_eur)} delta={pct(s.total_eur, p?.total_eur)} testid="crm-kpi-total" />
            <Kpi label="Scontrino medio" value={eur(s.avg_ticket)} delta={pct(s.avg_ticket, p?.avg_ticket)} testid="crm-kpi-avg" />
            <Kpi label="Carte attive" value={num(s.cards)} delta={pct(s.cards, p?.cards)} sub={s.cards ? `${(s.receipts / s.cards).toFixed(1)} scontrini/carta` : null} testid="crm-kpi-cards" />
            <Kpi label="Punti assegnati" value={num(s.points)} delta={pct(s.points, p?.points)} testid="crm-kpi-points" />
            <Kpi label="Punti usati" value={num(s.points_used)} testid="crm-kpi-used" />
            <Kpi label="Ora di punta" value={peakHour ? `${String(peakHour.hour).padStart(2, "0")}:00` : null} testid="crm-kpi-peak-hour" />
            <Kpi label="Giorno di punta" value={peakDay?.day} testid="crm-kpi-peak-day" />
          </div>

          <Panel title={data.days.length > 62 ? "Andamento mensile: scontrini e incasso" : "Andamento giornaliero: scontrini e incasso"} testid="crm-dash-trend">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
                <AreaChart data={trend} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f59e0b" stopOpacity={0.5} /><stop offset="100%" stopColor="#f59e0b" stopOpacity={0} /></linearGradient>
                    <linearGradient id="gRec" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3b82f6" stopOpacity={0.5} /><stop offset="100%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="label" {...AX} interval="preserveStartEnd" />
                  <YAxis yAxisId="l" {...AX} />
                  <YAxis yAxisId="r" orientation="right" {...AX} tickFormatter={(v) => `€${Math.round(v)}`} />
                  <Tooltip {...TT} formatter={(v, n) => [n === "Incasso" ? eur(v) : num(v), n]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area yAxisId="r" type="monotone" dataKey="total_eur" name="Incasso" stroke="#f59e0b" fill="url(#gRev)" strokeWidth={2} />
                  <Area yAxisId="l" type="monotone" dataKey="receipts" name="Scontrini" stroke="#3b82f6" fill="url(#gRec)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Panel title="Scontrini per fascia oraria" testid="crm-dash-hours">
              <div className="h-52"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
                <BarChart data={data.hours.map((h) => ({ ...h, label: String(h.hour).padStart(2, "0") }))} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} /><XAxis dataKey="label" {...AX} /><YAxis {...AX} />
                  <Tooltip {...TT} formatter={(v) => [num(v), "Scontrini"]} labelFormatter={(l) => `Ore ${l}:00`} /><Bar dataKey="receipts" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart></ResponsiveContainer></div>
            </Panel>
            <Panel title="Scontrini per giorno della settimana" testid="crm-dash-weekdays">
              <div className="h-52"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
                <BarChart data={data.weekdays} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} /><XAxis dataKey="day" {...AX} /><YAxis {...AX} />
                  <Tooltip {...TT} formatter={(v) => [num(v), "Scontrini"]} /><Bar dataKey="receipts" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart></ResponsiveContainer></div>
            </Panel>
            <Panel title="Distribuzione importo scontrino" testid="crm-dash-ticket-ranges">
              <div className="h-52"><ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
                <PieChart>
                  <Pie data={data.ticket_ranges} dataKey="receipts" nameKey="range" innerRadius={45} outerRadius={75} paddingAngle={2}>
                    {data.ticket_ranges.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip {...TT} formatter={(v, n) => [num(v), n]} /><Legend wrapperStyle={{ fontSize: 10 }} />
                </PieChart></ResponsiveContainer></div>
            </Panel>
          </div>

          {!fidOnly && (
            <Panel title="Scontrini fedeltà vs non fedeltà" testid="crm-dash-fidelity-share">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex-1 h-3 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full bg-amber-400" style={{ width: `${s.receipts ? (100 * s.fidelity_receipts / s.receipts) : 0}%` }} /></div>
                <div className="font-mono">{num(s.fidelity_receipts)} fedeltà ({s.receipts ? Math.round(100 * s.fidelity_receipts / s.receipts) : 0}%) · {num(s.non_fidelity_receipts)} anonimi</div>
              </div>
            </Panel>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Panel title="Top prodotti per incasso" testid="crm-dash-top-products" action={<Button size="sm" variant="ghost" onClick={exportProducts} data-testid="crm-dash-export-products"><Download className="h-3.5 w-3.5 mr-1" /> CSV</Button>}>
              <Table testid="crm-dash-products-table" rowKey="name" rows={data.top_products.slice(0, 15)} cols={[
                { k: "idx", l: "#", f: (_, __, i) => <span className="text-zinc-500">{i + 1}</span> },
                { k: "name", l: "Prodotto", f: (v) => <Link to={`/crm-prodotti?p=${encodeURIComponent(v)}&from=${range.date_from}&to=${range.date_to}&fid=${fidOnly ? 1 : 0}${stores.length ? `&stores=${encodeURIComponent(stores.join("|"))}` : ""}`} className="hover:underline" title="Apri analisi prodotto" data-testid={`crm-dash-product-link-${v.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>{v}</Link> },
                { k: "revenue", l: "Incasso", r: true, f: eur }, { k: "qty", l: "Q.tà", r: true, f: num }, { k: "customers", l: "Clienti", r: true, f: num }]} />
            </Panel>
            <Panel title="Top prodotti per quantità" testid="crm-dash-top-qty">
              <Table testid="crm-dash-qty-table" rowKey="name" rows={data.top_by_qty} cols={[
                { k: "idx", l: "#", f: (_, __, i) => <span className="text-zinc-500">{i + 1}</span> }, { k: "name", l: "Prodotto" },
                { k: "qty", l: "Q.tà", r: true, f: num }, { k: "lines", l: "Righe", r: true, f: num }, { k: "revenue", l: "Incasso", r: true, f: eur }]} />
            </Panel>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2">
              <Panel title="Top clienti per spesa" testid="crm-dash-top-customers" action={<Button size="sm" variant="ghost" onClick={exportCustomers} data-testid="crm-dash-export-customers"><Download className="h-3.5 w-3.5 mr-1" /> CSV</Button>}>
                <Table testid="crm-dash-customers-table" rowKey="card" rows={data.top_customers} cols={[
                  { k: "name", l: "Cliente", f: (v) => <span className="font-semibold">{v || "—"}</span> }, { k: "card", l: "Carta", f: (v) => <span className="font-mono">{v}</span> },
                  { k: "city", l: "Città" }, { k: "receipts", l: "Scontrini", r: true, f: num }, { k: "total_eur", l: "Speso", r: true, f: eur },
                  { k: "points", l: "Punti", r: true, f: num }, { k: "last_visit", l: "Ultima visita" }]} />
              </Panel>
            </div>
            <div className="space-y-4">
              <Panel title="Operatori di cassa" testid="crm-dash-operators">
                <Table testid="crm-dash-operators-table" rowKey="name" rows={data.operators.slice(0, 8)} cols={[
                  { k: "name", l: "Operatore" }, { k: "receipts", l: "Scontrini", r: true, f: num }, { k: "total_eur", l: "Incasso", r: true, f: eur }]} />
              </Panel>
              <Panel title="Tipo documento" testid="crm-dash-doc-types">
                <Table testid="crm-dash-doc-types-table" rowKey="name" rows={data.doc_types} cols={[
                  { k: "name", l: "Tipo" }, { k: "receipts", l: "N.", r: true, f: num }, { k: "total_eur", l: "Incasso", r: true, f: eur }]} />
              </Panel>
            </div>
          </div>
          <ElasticInactive />
        </>
      )}
    </div>
  );
}
